import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Descriptions, Table, Typography, Tag, Space, Spin, Tabs, Card, Alert, Button } from 'antd';
import { SupplierQuote } from '../types.ts';
import { supplierQuoteAPI } from '../services/api.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';
import { formatProductDisplay } from '../utils/productUtils.ts';
import { getQuoteStatusColor } from '../utils/quoteStatus.ts';
import QuoteHistoryTimeline from './QuoteHistoryTimeline.tsx';

const { Text } = Typography;
const { TabPane } = Tabs;

interface SupplierQuoteDetailsModalProps {
  quote: SupplierQuote | null;
  onClose: () => void;
}

// "Visualizza" preventivo fornitore: 4 tab come il modal Dettagli Ordine Fornitore.
// La riga dell'elenco porta del fornitore solo id e nome: il GET /{id} restituisce
// il fornitore completo per la scheda "Informazioni Fornitore".
const SupplierQuoteDetailsModal: React.FC<SupplierQuoteDetailsModalProps> = ({ quote, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [fullQuote, setFullQuote] = useState<SupplierQuote | null>(null);
  const [history, setHistory] = useState<any[]>([]);

  const loadQuote = useCallback(async () => {
    if (!quote) return;
    setLoading(true);
    try {
      const [data, events] = await Promise.all([
        supplierQuoteAPI.getSupplierQuote(quote.id),
        supplierQuoteAPI.getSupplierQuoteHistory(quote.id).catch(() => []),
      ]);
      setFullQuote(data);
      setHistory(events || []);
    } catch (error) {
      setFullQuote(quote);
    } finally {
      setLoading(false);
    }
  }, [quote]);

  useEffect(() => {
    if (quote) loadQuote();
  }, [quote, loadQuote]);

  if (!quote) return null;
  const q = fullQuote || quote;
  const supplierInfo: any = q.supplier;
  const details = q.quote_details || [];
  const computedTotal = details.reduce((sum, d) => sum + (d.total_cost || 0), 0);
  const fmtDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString() : 'Non specificata');

  const columns = [
    {
      title: 'Prodotto',
      key: 'product',
      width: 320,
      ellipsis: { showTitle: false },
      render: (_: any, record: any) => {
        if (!record.product) return <Text>N/A</Text>;
        const display = formatProductDisplay(record.product);
        return <Text code strong title={display}>{display}</Text>;
      },
    },
    { title: 'Qta', dataIndex: 'quantity', key: 'quantity', width: 70, align: 'center' as const, render: (v: number) => <Text strong>{v}</Text> },
    { title: 'Costo Unit.', dataIndex: 'unit_cost', key: 'unit_cost', width: 110, align: 'right' as const, render: (v: number) => `€ ${formatCurrency(v || 0)}` },
    { title: 'Totale', dataIndex: 'total_cost', key: 'total_cost', width: 110, align: 'right' as const,
      render: (v: number) => <Text strong style={{ color: '#1890ff' }}>€ {formatCurrency(v || 0)}</Text> },
    { title: 'Consegna gg', dataIndex: 'delivery_time_days', key: 'delivery_time_days', width: 100, align: 'center' as const, render: (v: number) => v != null ? `${v} gg` : '-' },
    { title: 'Note', dataIndex: 'notes', key: 'notes', ellipsis: true, render: (notes: string) => notes ? <Text type="secondary" title={notes}>{notes}</Text> : '-' },
  ];

  return (
    <Modal
      title={
        <Space>
          <Text strong style={{ fontSize: 18 }}>Dettagli Preventivo Fornitore</Text>
          <Tag color="blue" style={{ fontSize: 14 }}>{q.quote_number}</Tag>
        </Space>
      }
      open={true}
      onCancel={onClose}
      footer={null}
      width={1000}
      style={{ top: 20 }}
    >
      <Spin spinning={loading}>
        <Tabs defaultActiveKey="1">
          <TabPane tab="Informazioni Generali" key="1">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Numero Preventivo" span={2}><strong>{q.quote_number}</strong></Descriptions.Item>
              <Descriptions.Item label="Fornitore">{supplierInfo?.name || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Email Fornitore">{supplierInfo?.email || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Data Preventivo">{fmtDate(q.quote_date)}</Descriptions.Item>
              <Descriptions.Item label="Data Scadenza">{fmtDate(q.expiry_date)}</Descriptions.Item>
              <Descriptions.Item label="Giorni di Consegna">{q.delivery_time_days != null ? `${q.delivery_time_days} giorni` : 'Non specificati'}</Descriptions.Item>
              <Descriptions.Item label="Stato">
                <Tag color={getQuoteStatusColor(q.quote_status)}>{q.quote_status?.display_name || 'N/A'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Ordine Cliente Collegato" span={2}>
                {q.customer_order ? (
                  <Space direction="vertical" size={0}>
                    <Text strong>{q.customer_order.order_number}</Text>
                    {q.customer_order.customer_order_number && <Text type="secondary">{q.customer_order.customer_order_number}</Text>}
                    {q.customer_order.customer?.company && <Text type="secondary">{q.customer_order.customer.company}</Text>}
                  </Space>
                ) : 'Nessuno'}
              </Descriptions.Item>
              <Descriptions.Item label="Ordine Fornitore Generato" span={2}>
                {q.supplier_order ? (
                  <Space>
                    <Tag color="green">{q.supplier_order.order_number}</Tag>
                    <Text type="secondary">creato dalla conversione di questo preventivo</Text>
                  </Space>
                ) : 'Nessuno (preventivo non ancora convertito)'}
              </Descriptions.Item>
              <Descriptions.Item label="Importo Totale" span={2}>
                <strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  € {formatCurrency(q.total_amount != null ? q.total_amount : computedTotal)}
                </strong>
              </Descriptions.Item>
              {q.notes && (
                <Descriptions.Item label="Note" span={2}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{q.notes}</div>
                </Descriptions.Item>
              )}
            </Descriptions>
          </TabPane>

          <TabPane tab="Dettagli Prodotti" key="2">
            <Table dataSource={details} columns={columns} pagination={false} rowKey={(r: any) => r.id ?? r.product_id} size="small" />
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <strong>Totale Preventivo: € {formatCurrency(q.total_amount != null ? q.total_amount : computedTotal)}</strong>
            </div>
          </TabPane>

          <TabPane tab="Informazioni Fornitore" key="3">
            {supplierInfo ? (
              <Card size="small">
                <Descriptions bordered column={1}>
                  <Descriptions.Item label="Nome Fornitore">{supplierInfo.name}</Descriptions.Item>
                  {supplierInfo.supplier_code && <Descriptions.Item label="Codice Fornitore">{supplierInfo.supplier_code}</Descriptions.Item>}
                  {supplierInfo.contact_person && <Descriptions.Item label="Persona di Contatto">{supplierInfo.contact_person}</Descriptions.Item>}
                  {supplierInfo.email && <Descriptions.Item label="Email">{supplierInfo.email}</Descriptions.Item>}
                  {supplierInfo.phone && <Descriptions.Item label="Telefono">{supplierInfo.phone}</Descriptions.Item>}
                  {supplierInfo.address && (
                    <Descriptions.Item label="Indirizzo">
                      {supplierInfo.address}
                      {supplierInfo.city && `, ${supplierInfo.city}`}
                      {supplierInfo.postal_code && ` ${supplierInfo.postal_code}`}
                      {supplierInfo.country && `, ${supplierInfo.country}`}
                    </Descriptions.Item>
                  )}
                  {supplierInfo.website && (
                    <Descriptions.Item label="Sito Web">
                      <a href={supplierInfo.website} target="_blank" rel="noopener noreferrer">{supplierInfo.website}</a>
                    </Descriptions.Item>
                  )}
                  {supplierInfo.notes && <Descriptions.Item label="Note Fornitore">{supplierInfo.notes}</Descriptions.Item>}
                </Descriptions>
              </Card>
            ) : (
              <Alert message="Informazioni fornitore non disponibili" type="info" />
            )}
          </TabPane>

          <TabPane tab="Cronologia" key="4">
            <QuoteHistoryTimeline events={history} />
          </TabPane>
        </Tabs>

        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Button onClick={onClose}>Chiudi</Button>
        </div>
      </Spin>
    </Modal>
  );
};

export default SupplierQuoteDetailsModal;
